import { createSlice } from '@reduxjs/toolkit';

type MyDictionary = Record<string, any>;

const patientInformation = {
  type: '',
  gender: '',
  age: {
    years: 0,
    months: 0
  },
  dob: {
    day: 0,
    month: 0,
    year: 0
  },
  metric: {
    height: 0,
    weight: 0,
    usualWeight: 0,
    estimatedDryWeight: 0
  },
  imperial: {
    height: { ft: 0, in: 0 },
    weight: { st: 0, lb: 0 },
    usualWeight: { st: 0, lb: 0 },
    estimatedDryWeight: { st: 0, lb: 0 }
  },
  bmi: 0,
  lossPercentage: 0
};

const alternativeMeasures = {
  ulnaLength: 0,
  estHeightUlna: 0,
  estBMI_muac: 0,
  muac: 0,
  estBMI_ulna: 0
};

const optedOptions = {
  routeObj: {
    estimating_requirements: false,
    malnutrition_screening: false,
    muscle_screening: false
  },
  routeArr: []
};

const peng2018 = {
  disease: {},
  ree2ndVal: [],
  palValue: 0,
  reePlusPal: []
};

const simpleCaloricEquationData = {
  calorieRange: {
    energy: { min: 0, max: 0 }
  },
  min: 0,
  max: 0
};

const faisyEquationData = {
  ve: 0,
  tMax: 0,
  rmr: 0
};

const glim = {
  phenoCrtiteria: true,
  isPatientHealthy: 'one',
  isPatientHasOedema: true,
  reduceMuscleMass: true,
  calfCircumference: 0,
  bmiOption: '',
  etiologicalCriteria: {
    reducedNutritionIntake: true,
    isPatientInjured: true,
    isPatientHasIntestineIssue: true
  },
  phenotypicScore: 0,
  etiologicalscore: 0
};

const pennStateData = {
  ve: 0,
  tMax: 0,
  rmr: 0
};

const modifiedPennStateData = {
  ve: 0,
  tMax: 0,
  rmr: 0
};

const malnutritionScreening = {
  mst: {
    unintentionalWeightLoss: 0,
    weightLoss: {
      optionSelected: '',
      value: 0
    },
    reducedFoodIntake: 0,
    totalScore: 0
  },
  must: {
    bmiScore: 0,
    weightLossScore: 0,
    acuteIllnessScore: 0,
    totalScore: 0
  },
  glim: glim
};

const muscleScreening = {
  sarcf: {
    power: '',
    walking: '',
    chair: '',
    stairs: '',
    fall: '',
    totalScore: 0
  },
  calfCircumference: {
    calfCircumferenceVal: 0,
    isPatientHealthy: 'one',
    isPatientHasOedema: true,
    calculatedCircumference: 0
  },
  handGripStrength: 0
};

const mifflinStJeor = {
  bmr: 0,
  selection: { activityFactor: true },
  activityFactor: { optionSelected: '', value: 0, bmrPlusActivityFactor: 0 },
  activityFactorPAL_Select: {
    occupationalActivityLevel: '',
    nonOccupationalActivityLevel: '',
    value: 0,
    bmrPlusPALSelect: 0
  },
  activityFactorPAL_Input: { PALRatio: 1, value: 0, bmrPlusPALInput: 0 },
  summary: 0
};

const henryOxford = {
  bmr: 0,
  selection: { stressFactor: true, activityFactor: true },
  stressFactor: {
    optionSelected: 0,
    SFRange: '',
    adjustedValue: 0,
    SFValue: 0,
    bmrPlusStressFactor: 0
  },
  activityFactor: { optionSelected: '', value: 0, bmrPlusActivityFactor: 0 },
  activityFactorPAL_Select: {
    occupationalActivityLevel: '',
    nonOccupationalActivityLevel: '',
    value: 0,
    bmrPlusPALSelect: 0
  },
  activityFactorPAL_Input: { PALRatio: 1, value: 0, bmrPlusPALInput: 0 },
  summary: 0
};

const fluids = {
  require1: 0,
  require2: 0,
  temperature: 37,
  additionalFluids: 1,
  fluidLoss: 0
};

const paediatricProteinData = {
  proteinRequirement: 0,
  proteinRequirementForWeight: 0,
  efsa: {
    ar: 0,
    pri: 0
  },
  coma: {
    ear: 0,
    rni: 0
  }
};

const paediatricEnergyData = {
  childType: '',
  tabIndex: 0,
  activityLevel: '',
  energyRequirement: {
    energyForAge: 0,
    energyForAgePerKg: 0,
    energyForWeight: 0,
    energyForWeightPerKg: 0
  }
};

const routeData = {
  fillMandatoryFields: false,
  lastClickedTile: ''
};

const protein = {
  min: 1,
  max: 1.5,
  calculatedMin: 0,
  calculatedMax: 0
};

const paedFluidData = {
  fluidRequirementsInput: 0,
  calculatedFluidRequirements: 0,
  holidaySegar: {
    fluidMlPerDay: 0,
    fluidMlPerKgPerDay: 0
  },
  efsaFluidRequirement: [],
  iomRequirement: 0
};

const compare = {
  checkedItems: {},
  selectedProducts: [],
  compareData: [],
  bottleInputP1: 0,
  bottleInputP2: 0,
  volumeInputP1: 0,
  volumeInputP2: 0,
  p1VolumeCalculationOffset: 0,
  p2VolumeCalculationOffset: 0
};

const rni = {
  checkedItems: {},
  selectedProducts: [],
  rniData: [],
  bottleInputP1: 0,
  bottleInputP2: 0,
  volumeInputP1: 0,
  volumeInputP2: 0,
  p1VolumeCalculationOffset: 0,
  p2VolumeCalculationOffset: 0
};

const summaryData = {
  age: 0,
  weight: 0,
  height: 0,
  bmi: 0,
  selectedAdultEnergy: {
    name: '',
    value: 0
  },
  selectedPaediatricEnergy: {
    name: '',
    value: 0
  },
  selectedPaediatricProtein: {
    name: '',
    value: 0
  },
  estimatingRequirements: {
    peng2018: 0,
    henryOxford: 0,
    harrisBenedict: 0,
    simpleCaloricEquation: {
      min: 0,
      max: 0
    },
    pennState: 0,
    modifiedPennState: 0,
    faisyEquation: 0,
    mifflinStJeor: 0,
    protein: {
      min: 0,
      max: 0
    },
    fluids: 0
  },
  malnutritionScreening: {
    must: 0,
    glim: 0,
    mst: 0
  },
  muscleScreening: {
    sarcf: 0,
    calfCircumference: 0,
    handgripStrength: 0
  },
  paediatricEPF: {
    energyForAge: 0,
    energyForAgePerKg: 0,
    energyForWeight: 0,
    energyForWeightPerKg: 0,
    ar: 0,
    pri: 0,
    ear: 0,
    rni: 0,
    arPerKg: 0,
    priPerKg: 0,
    earPerKg: 0,
    rniPerKg: 0,
    fluidMlPerDay: 0,
    fluidMlPerKgPerDay: 0,
    efsa: 0,
    iom: 0
  }
};

const initialState: MyDictionary = {
  routeData: routeData,
  patientInformation: patientInformation,
  alternativeMeasures: alternativeMeasures,
  optedOptions: optedOptions,
  estimatingRequirements: {
    henryOxford: henryOxford,
    harrisBenedict: { ...henryOxford },
    mifflinStJeor: mifflinStJeor,
    peng2018: peng2018,
    fluids: fluids,
    protein: protein,
    simpleCaloricEquation: simpleCaloricEquationData,
    faisyEquation: faisyEquationData,
    pennState: pennStateData,
    modifiedPennState: modifiedPennStateData,
    paedFluid: paedFluidData,
    paediatricProtein: paediatricProteinData,
    paediatricEnergy: paediatricEnergyData
  },
  malnutritionScreening: malnutritionScreening,
  muscleScreening: muscleScreening,
  compare: compare,
  rni: rni,
  summary: summaryData
};

const metaDataSlice = createSlice({
  name: 'metaData',
  initialState,
  reducers: {
    setRouteData: (state, action) => {
      state.routeData = action.payload;
    },
    setPatientInformation: (state, action) => {
      state.patientInformation = action.payload;
    },
    setHenryOxfordSelection: (state, action) => {
      state.estimatingRequirements.henryOxford.selection = action.payload;
    },
    setHenryOxfordBMR: (state, action) => {
      state.estimatingRequirements.henryOxford.bmr = action.payload;
    },
    setHenryStressFactor: (state, action) => {
      state.estimatingRequirements.henryOxford.stressFactor = action.payload;
    },
    setHenryActivityFactor: (state, action) => {
      state.estimatingRequirements.henryOxford.activityFactor = action.payload;
    },
    setHenryActivityFactorPAL_Select: (state, action) => {
      state.estimatingRequirements.henryOxford.activityFactorPAL_Select =
        action.payload;
    },
    setHenryActivityFactorPAL_Input: (state, action) => {
      state.estimatingRequirements.henryOxford.activityFactorPAL_Input =
        action.payload;
    },
    setHenrySummary: (state, action) => {
      state.estimatingRequirements.henryOxford.summary = action.payload;
    },
    setHarrisSelection: (state, action) => {
      state.estimatingRequirements.harrisBenedict.selection = action.payload;
    },
    setHarrisBMR: (state, action) => {
      state.estimatingRequirements.harrisBenedict.bmr = action.payload;
    },
    setHarrisStressFactor: (state, action) => {
      state.estimatingRequirements.harrisBenedict.stressFactor = action.payload;
    },
    setHarrisActivityFactor: (state, action) => {
      state.estimatingRequirements.harrisBenedict.activityFactor =
        action.payload;
    },
    setHarrisActivityFactorPAL_Select: (state, action) => {
      state.estimatingRequirements.harrisBenedict.activityFactorPAL_Select =
        action.payload;
    },
    setHarrisActivityFactorPAL_Input: (state, action) => {
      state.estimatingRequirements.harrisBenedict.activityFactorPAL_Input =
        action.payload;
    },
    setHarrisSummary: (state, action) => {
      state.estimatingRequirements.harrisBenedict.summary = action.payload;
    },
    setMifflinSelection: (state, action) => {
      state.estimatingRequirements.mifflinStJeor.selection = action.payload;
    },
    setMifflinBMR: (state, action) => {
      state.estimatingRequirements.mifflinStJeor.bmr = action.payload;
    },
    setMifflinActivityFactor: (state, action) => {
      state.estimatingRequirements.mifflinStJeor.activityFactor =
        action.payload;
    },
    setMifflinActivityFactorPAL_Select: (state, action) => {
      state.estimatingRequirements.mifflinStJeor.activityFactorPAL_Select =
        action.payload;
    },
    setMifflinActivityFactorPAL_Input: (state, action) => {
      state.estimatingRequirements.mifflinStJeor.activityFactorPAL_Input =
        action.payload;
    },
    setMifflinSummary: (state, action) => {
      state.estimatingRequirements.mifflinStJeor.summary = action.payload;
    },
    setFluids: (state, action) => {
      state.estimatingRequirements.fluids = action.payload;
    },
    setFluidsRequire: (state, action) => {
      state.estimatingRequirements.fluids.require2 = action.payload;
    },
    setPaediatricProtein: (state, action) => {
      state.estimatingRequirements.paediatricProtein = action.payload;
    },
    setPaediatricEnergy: (state, action) => {
      state.estimatingRequirements.paediatricEnergy = action.payload;
    },
    setUlna: (state, action) => {
      state.alternativeMeasures.ulnaLength = action.payload;
    },
    setMuac: (state, action) => {
      state.alternativeMeasures.muac = action.payload;
    },
    setEstBmiMuac: (state, action) => {
      state.alternativeMeasures.estBMI_muac = action.payload;
    },
    setEstBmiUlna: (state, action) => {
      state.alternativeMeasures.estBMI_ulna = action.payload;
    },
    setEstimatedHeight: (state, action) => {
      state.alternativeMeasures.estHeightUlna = action.payload;
    },
    setRouteObj: (state, action) => {
      state.optedOptions.routeObj = action.payload;
    },
    setRouteArr: (state, action) => {
      state.optedOptions.routeArr = action.payload;
    },
    resetPengValues: (state) => {
      state.estimatingRequirements.peng2018 = peng2018;
    },
    setPengDisease: (state, action) => {
      state.estimatingRequirements.peng2018.disease = action.payload;
    },
    setPengReeSecondVal: (state, action) => {
      state.estimatingRequirements.peng2018.ree2ndVal = action.payload;
    },
    setPengPal: (state, action) => {
      state.estimatingRequirements.peng2018.palValue = action.payload;
    },
    setPengReePlusPal: (state, action) => {
      state.estimatingRequirements.peng2018.reePlusPal = action.payload;
    },
    setSimpleCaloricSelection: (state, action) => {
      state.estimatingRequirements.simpleCaloricEquation = action.payload;
    },
    setFaisySelection: (state, action) => {
      state.estimatingRequirements.faisyEquation = action.payload;
    },
    setGlim: (state, action) => {
      state.malnutritionScreening.glim = action.payload;
    },
    setPennSelection: (state, action) => {
      state.estimatingRequirements.pennState = action.payload;
    },
    setModifiedPennSelection: (state, action) => {
      state.estimatingRequirements.modifiedPennState = action.payload;
    },
    setMSTSelection: (state, action) => {
      state.malnutritionScreening.mst = action.payload;
    },
    setMustValues: (state, action) => {
      state.malnutritionScreening.must = action.payload;
    },
    setSarcfValues: (state, action) => {
      state.muscleScreening.sarcf = action.payload;
    },
    setHGSSelection: (state, action) => {
      state.muscleScreening = action.payload;
    },
    setCalfCircum: (state, action) => {
      state.muscleScreening.calfCircumference = action.payload;
    },
    setProteinCalc: (state, action) => {
      state.estimatingRequirements.protein = action.payload;
    },
    setPaedFluidCalc: (state, action) => {
      state.estimatingRequirements.paedFluid = action.payload;
    },
    setSummaryData: (state, action) => {
      state.summary = action.payload;
    },
    setCompareData: (state, action) => {
      state.compare = action.payload;
    },
    setCompareP1VolumeOffset: (state, action) => {
      state.compare.p1VolumeCalculationOffset = action.payload;
    },
    setCompareP2VolumeOffset: (state, action) => {
      state.compare.p2VolumeCalculationOffset = action.payload;
    },
    setRniData: (state, action) => {
      state.rni = action.payload;
    },
    setRniP1VolumeOffset: (state, action) => {
      state.rni.p1VolumeCalculationOffset = action.payload;
    },
    setRniP2VolumeOffset: (state, action) => {
      state.rni.p2VolumeCalculationOffset = action.payload;
    },
    resetPatientData: () => initialState
  }
});

export default metaDataSlice.reducer;

export const {
  setRouteData,
  setPatientInformation,
  setHenryOxfordSelection,
  setHenryOxfordBMR,
  setHenryStressFactor,
  setHenryActivityFactor,
  setHenryActivityFactorPAL_Select,
  setHenryActivityFactorPAL_Input,
  setHenrySummary,
  setHarrisSelection,
  setHarrisBMR,
  setHarrisStressFactor,
  setHarrisActivityFactor,
  setHarrisActivityFactorPAL_Select,
  setHarrisActivityFactorPAL_Input,
  setHarrisSummary,
  setMifflinSelection,
  setMifflinBMR,
  setMifflinActivityFactor,
  setMifflinActivityFactorPAL_Select,
  setMifflinActivityFactorPAL_Input,
  setMifflinSummary,
  setFluids,
  setFluidsRequire,
  setPaediatricProtein,
  setPaediatricEnergy,
  setUlna,
  setMuac,
  setEstBmiMuac,
  setEstBmiUlna,
  setEstimatedHeight,
  resetPengValues,
  setPengDisease,
  setPengReeSecondVal,
  setPengPal,
  setPengReePlusPal,
  setRouteObj,
  setSimpleCaloricSelection,
  setFaisySelection,
  setGlim,
  setPennSelection,
  setModifiedPennSelection,
  setMSTSelection,
  setMustValues,
  setRouteArr,
  setSarcfValues,
  setHGSSelection,
  setCalfCircum,
  setProteinCalc,
  setPaedFluidCalc,
  setSummaryData,
  setCompareData,
  setCompareP1VolumeOffset,
  setCompareP2VolumeOffset,
  setRniData,
  setRniP1VolumeOffset,
  setRniP2VolumeOffset,
  resetPatientData
} = metaDataSlice.actions;
